import React, { useState, useRef, useEffect } from "react"
import {
  HeartIcon,
  MegaphoneIcon,
  UserPlusIcon,
  ArrowPathIcon,
  LinkIcon,
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CubeIcon,
} from "@heroicons/react/24/outline"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import { INITIATIVE_TYPE_LABELS } from "~/ui-rtk/constants/initiative-types"

// Option styling configuration type
interface OptionConfig {
  icon?: React.ReactNode
  color?: string
  bgColor?: string
  hoverBg?: string
  borderColor?: string
  hoverBorderColor?: string
  label?: string
}

// Default initiative type mappings for custom styling
const DEFAULT_OPTION_CONFIGS: Record<string, OptionConfig> = {
  [InitiativeType.RETENTION]: {
    icon: <HeartIcon className="w-4 h-4" />,
    color: "text-system-success-300",
    bgColor: "bg-system-success-500/10",
    hoverBg: "hover:bg-system-success-500/20",
    borderColor: "border-system-success-600/40",
    hoverBorderColor: "hover:border-system-success-500/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.RETENTION],
  },
  [InitiativeType.DEMAND_GENERATION]: {
    icon: <MegaphoneIcon className="w-4 h-4" />,
    color: "text-system-cyan-200",
    bgColor: "bg-system-cyan-500/10",
    hoverBg: "hover:bg-system-cyan-500/20",
    borderColor: "border-system-cyan-500/40",
    hoverBorderColor: "hover:border-system-cyan-400/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION],
  },
  [InitiativeType.DEMAND_GENERATION_2]: {
    icon: <MegaphoneIcon className="w-4 h-4" />,
    color: "text-system-magenta-200",
    bgColor: "bg-system-magenta-500/10",
    hoverBg: "hover:bg-system-magenta-500/20",
    borderColor: "border-system-magenta-500/40",
    hoverBorderColor: "hover:border-system-magenta-400/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION_2],
  },
  [InitiativeType.DEMAND_CAPTURE_ACQUISITION]: {
    icon: <UserPlusIcon className="w-4 h-4" />,
    color: "text-system-sea-300",
    bgColor: "bg-system-sea-500/10",
    hoverBg: "hover:bg-system-sea-500/20",
    borderColor: "border-system-sea-600/40",
    hoverBorderColor: "hover:border-system-sea-500/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_ACQUISITION],
  },
  [InitiativeType.DEMAND_CAPTURE_RETARGETING]: {
    icon: <ArrowPathIcon className="w-4 h-4" />,
    color: "text-system-orange-200",
    bgColor: "bg-system-orange-500/10",
    hoverBg: "hover:bg-system-orange-500/20",
    borderColor: "border-system-orange-500/40",
    hoverBorderColor: "hover:border-system-orange-400/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_RETARGETING],
  },
  [InitiativeType.DEMAND_CAPTURE_BROAD]: {
    icon: <LinkIcon className="w-4 h-4" />,
    color: "text-system-yellow-200",
    bgColor: "bg-system-yellow-500/10",
    hoverBg: "hover:bg-system-yellow-500/20",
    borderColor: "border-system-yellow-500/40",
    hoverBorderColor: "hover:border-system-yellow-400/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_BROAD],
  },
  [InitiativeType.BRANDED_SEARCH]: {
    icon: <MagnifyingGlassIcon className="w-4 h-4" />,
    color: "text-system-indigo-200",
    bgColor: "bg-system-indigo-500/10",
    hoverBg: "hover:bg-system-indigo-500/20",
    borderColor: "border-system-indigo-600/40",
    hoverBorderColor: "hover:border-system-indigo-500/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.BRANDED_SEARCH],
  },
  [InitiativeType.NON_BRAND_SEARCH]: {
    icon: <MagnifyingGlassIcon className="w-4 h-4" />,
    color: "text-system-violet-200",
    bgColor: "bg-system-violet-500/10",
    hoverBg: "hover:bg-system-violet-500/20",
    borderColor: "border-system-violet-600/40",
    hoverBorderColor: "hover:border-system-violet-500/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.NON_BRAND_SEARCH],
  },
  [InitiativeType.PMAX]: {
    icon: <CubeIcon className="w-4 h-4" />,
    color: "text-system-fuchsia-200",
    bgColor: "bg-system-fuchsia-500/10",
    hoverBg: "hover:bg-system-fuchsia-500/20",
    borderColor: "border-system-fuchsia-600/40",
    hoverBorderColor: "hover:border-system-fuchsia-500/60",
    label: INITIATIVE_TYPE_LABELS[InitiativeType.PMAX],
  },
  // Custom options
  Exclude: {
    icon: <XMarkIcon className="w-4 h-4" />,
    color: "text-system-red-300",
    bgColor: "bg-system-red-500/10",
    hoverBg: "hover:bg-system-red-500/20",
    borderColor: "border-system-red-600/40",
    hoverBorderColor: "hover:border-system-red-500/60",
    label: "Exclude",
  },
}

interface InitiativeTypeDropdownProps {
  value?: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
  onStopPropagation: (e: React.MouseEvent) => void
  customConfigs?: Record<string, OptionConfig>
  placeholder?: string
}

export default function InitiativeTypeDropdown({
  value,
  options,
  onChange,
  onStopPropagation,
  customConfigs = {},
  placeholder = "Select Initiative Type...",
}: InitiativeTypeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Merge default configs with custom configs
  const allConfigs = { ...DEFAULT_OPTION_CONFIGS, ...customConfigs }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Get config for the selected option
  const selectedConfig = value ? allConfigs[value] : null

  // Custom initiative types have no built-in style config, but they are present
  // in `options` with their display label. Fall back to that label so the
  // trigger reflects the selected value instead of the placeholder.
  const fallbackLabel =
    value && !selectedConfig
      ? options.find(option => option.value === value)?.label
      : undefined

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onClick={onStopPropagation}
      onMouseDown={onStopPropagation}
    >
      {/* Trigger Button */}
      <button
        type="button"
        onClick={e => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-md transition-colors text-left ${
          selectedConfig
            ? `${selectedConfig.bgColor} ${selectedConfig.hoverBg} ${selectedConfig.borderColor} ${selectedConfig.hoverBorderColor}`
            : "bg-system-blue-600/20 border-system-blue-600/30 hover:border-system-blue-400/50"
        }`}
      >
        {selectedConfig ? (
          <div className="flex items-center gap-2">
            <span className={selectedConfig.color}>{selectedConfig.icon}</span>
            <span className="text-white text-sm font-medium">
              {selectedConfig.label}
            </span>
          </div>
        ) : fallbackLabel ? (
          <span className="text-white text-sm font-medium">
            {fallbackLabel}
          </span>
        ) : (
          <span className="text-system-gray-400 text-sm">{placeholder}</span>
        )}
        <ChevronDownIcon
          className={`w-4 h-4 text-system-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-system-blue-800 border border-system-blue-600/50 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
          {options.map(option => {
            const config = option.value ? allConfigs[option.value] : null

            return (
              <button
                key={option.value}
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  handleOptionClick(option.value)
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${(() => {
                  if (option.value === value) {
                    return config
                      ? `${config.bgColor} ${config.color}`
                      : "bg-system-blue-600/20 text-white"
                  }
                  if (config) return `text-system-gray-300 ${config.hoverBg}`
                  if (option.value === "") {
                    return "text-system-gray-300 hover:bg-system-red-600/10 hover:text-system-red-300"
                  }
                  return "text-system-gray-300 hover:bg-system-blue-600/10"
                })()} hover:text-white`}
              >
                {config && <span className={config.color}>{config.icon}</span>}
                {!config &&
                  option.value === "" &&
                  option.label.includes("Clear") && (
                    <span className="text-system-red-400">✕</span>
                  )}
                <span
                  className={`text-sm font-medium ${
                    !config &&
                    option.value === "" &&
                    option.label.includes("Clear")
                      ? "text-system-red-300"
                      : ""
                  }`}
                >
                  {config ? config.label : option.label}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
